import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useToast, formatDate, StatusBadge } from '../components/shared';
import { DeviceLogForm, RESULT, everyLabel, todayStr } from '../components/PmLog';
import Photo from '../components/Photo';

export default function DeviceScan() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [logging, setLogging] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();
  const go = (e, to) => { e.preventDefault(); navigate(to); };

  const load = useCallback(() => api.pmScan(token).then(d => { setData(d); setError(''); }).catch(e => setError(e.message)), [token]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="page" dir="rtl"><div className="card" style={{ padding: 24, textAlign: 'center' }}>
    <div style={{ fontSize: 40 }}>🔍</div><div className="fw-600 mt-8">{error === 'Device not found' ? 'الكود ده مش مربوط بأي جهاز' : error}</div></div></div>;
  if (!data) return <div className="page"><div className="empty">Loading...</div></div>;

  const { device: d, contracts, history, tickets, user } = data;
  const canLog = ['admin', 'technician'].includes(user.role);
  const t = todayStr();

  async function log(k, body) {
    await api.pmLogItem(k.current_visit.id, { contract_device_id: k.contract_device_id, ...body });
    toast('اتسجلت الصيانة ✓'); setLogging(null); load();
  }

  return (
    <div className="page" dir="rtl" style={{ maxWidth: 760 }}>
      <div className="card mb-16" style={{ padding: 16, borderTop: '4px solid var(--primary)' }}>
        <div className="text-xs text-dim">🛠 صفحة الجهاز · {user.username}</div>
        <div className="flex gap-12 mt-8" style={{ alignItems: 'flex-start' }}>
          <Photo kind="device" id={d.id} version={d.photo_at} size={110} icon="📟" canEdit={canLog} canDelete={user.role === 'admin'} onChange={load} />
          <div style={{ minWidth: 0 }}>
            <div className="fw-600" style={{ fontSize: 22 }}>{d.name}{d.alias ? <span className="text-muted" style={{ fontSize: 15 }}> · {d.alias}</span> : null}</div>
            <div className="mono text-sm mt-4" dir="ltr" style={{ textAlign: 'right' }}>S/N: {d.serial}</div>
            {!d.photo_at && canLog && <div className="text-xs text-dim mt-4">📷 اضغط على المربع وصوّر الجهاز</div>}
          </div>
        </div>
        {d.client_name && <div className="flex items-center gap-8 text-sm mt-8">
          {d.client_id && <Photo kind="client" id={d.client_id} version={d.client_photo_at} size={36} icon="🏥" canEdit={canLog} onChange={load} />}
          <span>{d.client_name}</span>{d.client_phone && <> · <a className="link" href={`tel:${d.client_phone}`}>{d.client_phone}</a></>}</div>}
        {d.notes && <div className="text-sm text-muted mt-8">{d.notes}</div>}
      </div>

      <div className="section-title mb-8">الصيانة الدورية</div>
      {contracts.length === 0 && <div className="card mb-16" style={{ padding: 16 }}><span className="text-dim">الجهاز ده مش داخل في أي عقد صيانة.</span></div>}
      {contracts.map(k => {
        const cv = k.current_visit;
        const late = cv && cv.scheduled_date < t;
        return (
          <div key={k.id} className="card mb-16" style={{ padding: 16, opacity: k.status === 'active' ? 1 : 0.6 }}>
            <div className="flex items-center gap-8" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <a className="link fw-600" href={`/maintenance/${k.id}`} onClick={e => go(e, `/maintenance/${k.id}`)}>📄 عقد {k.client_name}</a>
              <span className="text-xs text-dim">{formatDate(k.start_date)} ← {formatDate(k.end_date)} · {everyLabel(k.every_value, k.every_unit)}</span>
            </div>
            {!cv && <div className="text-sm text-muted mt-8">✓ مفيش زيارات مفتوحة للجهاز ده في العقد.</div>}
            {cv && <>
              <div className="flex items-center gap-8 mt-8" style={{ flexWrap: 'wrap' }}>
                <span className="fw-600">زيارة {cv.seq} من {k.visits_total}</span>
                <span className={`badge ${late ? 'badge-red' : 'badge-blue'}`}>{late ? 'متأخرة' : ''} {formatDate(cv.scheduled_date)}</span>
                <a className="link text-xs" style={{ marginInlineStart: 'auto' }} href={`/maintenance/visit/${cv.id}`} onClick={e => go(e, `/maintenance/visit/${cv.id}`)}>الزيارة كلها ←</a>
              </div>
              {logging !== k.id && (
                <ul className="text-sm" style={{ margin: '8px 0 0', paddingInlineStart: 18, color: 'var(--text2)' }}>
                  {cv.due_tasks.map(x => <li key={x.id}>{x.text}</li>)}
                </ul>
              )}
              {canLog && k.status === 'active' && logging !== k.id && (
                <button className="btn btn-primary mt-8" style={{ width: '100%', padding: 12 }} onClick={() => setLogging(k.id)}>✓ سجّل صيانة الجهاز</button>
              )}
              {canLog && k.status === 'active' && logging !== k.id && (
                <button className="btn btn-ghost mt-8" style={{ width: '100%' }} onClick={() => navigate(`/maintenance/visit/${cv.id}`)}>🔧 إصلاح / قطع غيار في الزيارة دي</button>
              )}
              {logging === k.id && <DeviceLogForm tasks={cv.due_tasks} onSubmit={b => log(k, b)} onCancel={() => setLogging(null)} />}
            </>}
          </div>
        );
      })}

      <div className="section-title mb-8">سجل الصيانة</div>
      <div className="card mb-16" style={{ padding: 0 }}>
        {history.length === 0 ? <div className="empty text-sm">لسه مفيش</div> : history.map(h => {
          const [l, cls] = RESULT[h.result] || [h.result, 'badge-gray'];
          const done = h.checklist.filter(c => c.ok).length;
          return (
            <div key={h.id} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
              <div className="flex items-center gap-8" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <span className="fw-600 text-sm">{formatDate(h.done_at)} · زيارة {h.seq} · {h.client_name}</span>
                <span className={`badge ${cls}`}>{l}</span>
              </div>
              <div className="text-xs text-dim">👤 {h.done_by || '—'}{h.checklist.length ? ` · ${done}/${h.checklist.length} مهمة` : ''}</div>
              {h.notes && <div className="text-sm mt-4">{h.notes}</div>}
            </div>
          );
        })}
      </div>

      {tickets.length > 0 && <>
        <div className="section-title mb-8">تذاكر الإصلاح</div>
        <div className="card" style={{ padding: 0 }}>
          {tickets.map(x => (
            <div key={x.id} className="flex items-center gap-8" onClick={() => navigate(`/tickets/${x.id}`)}
              style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
              <span className="text-sm" style={{ flex: 1 }}>{x.fault_description || '—'}</span>
              <span className="text-xs text-dim">{formatDate(x.received_at)}</span>
              <StatusBadge status={x.status} />
            </div>
          ))}
        </div>
      </>}
    </div>
  );
}
